export interface OperatorDef {
  value: string;
  label: string;
}

export interface FilterFieldDef {
  name: string;
  key: string;
  type: 'list' | 'text' | 'date' | 'number' | 'boolean';
  category: string;
  operators: OperatorDef[];
  // Presence-style boolean: the field has no boolean column on the BFF, so the
  // Yes/No choice maps to `isNotEmpty`/`isEmpty` on an aggregated string column
  // (e.g. Parts Pending → `pendedParts`) rather than `equals,true/false`.
  presence?: boolean;
  // Date fields only: render a date + time picker (defaults to 00:00, adjustable)
  // and carry the time through to the wire, instead of a date-only picker.
  // Opt-in per field so other lists keep date-only filtering; only set it where
  // the underlying column is a timestamp. See AddFilterConfigure.
  withTime?: boolean;
}

export const OPS_TEXT_FULL: OperatorDef[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isEmpty', label: 'Is empty' },
];

export const OPS_TEXT_CONTAINS: OperatorDef[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'isEmpty', label: 'Is empty' },
];

export const OPS_TEXT_ID: OperatorDef[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
];

// Text operators plus `isAnyOf`, for fields where pasting a batch of values
// (e.g. a column of serials copied from a spreadsheet) is a real workflow.
// Selecting `Is any of` swaps the single text box for a multi-value paste
// input; the values serialise to `field=isAnyOf,a,b,c` — the same shape the
// status / Job ID filters already use.
export const OPS_TEXT_BULK: OperatorDef[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isAnyOf', label: 'Is any of' },
  { value: 'isEmpty', label: 'Is empty' },
];

export const OPS_NUM_FULL: OperatorDef[] = [
  { value: 'equals', label: 'Equals' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'between', label: 'Between' },
  { value: 'isEmpty', label: 'Is empty' },
];

export const OPS_NUM_SIMPLE: OperatorDef[] = [
  { value: 'equals', label: 'Equals' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
];

export const OPS_LIST: OperatorDef[] = [
  { value: 'isAnyOf', label: 'Is any of' },
  { value: 'not', label: 'Is not' },
];

export const OPS_DATE: OperatorDef[] = [{ value: 'between', label: 'Between' }];

// Operator values map directly to what the BFF list endpoints expect on the
// query string (e.g. `closedAt=onOrAfter,…`). `between` is the one synthetic
// option — `buildFilterModel` splits it into `onOrAfter` + `onOrBefore`.
export const OPS_DATE_FULL: OperatorDef[] = [
  { value: 'between', label: 'Between' },
  { value: 'is', label: 'On' },
  { value: 'not', label: 'Is not' },
  { value: 'after', label: 'After' },
  { value: 'onOrAfter', label: 'On or after' },
  { value: 'before', label: 'Before' },
  { value: 'onOrBefore', label: 'On or before' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
];

export const OPS_BOOLEAN: OperatorDef[] = [{ value: 'equals', label: 'Equals' }];

export const FILTER_FIELD_DEFINITIONS: FilterFieldDef[] = [
  // ── Service/Job ──────────────────────────────────────────────
  {
    name: 'Job ID',
    key: 'serviceId',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_ID,
  },
  {
    name: 'Service Type',
    key: 'serviceTypeName',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Service Type Code',
    key: 'serviceTypeCode',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Action Type',
    key: 'actionTypeName',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Process Type',
    key: 'processTypeName',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Service Params',
    key: 'serviceParams',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Service Zone',
    key: 'serviceZone',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Reference #1',
    key: 'reference1',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Reference #2',
    key: 'reference2',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Reference #3',
    key: 'reference3',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Client',
    key: 'clientHierarchy',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Client Name',
    key: 'clientName',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Client Code',
    key: 'clientCode',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Contract',
    key: 'contractName',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Contract Code',
    key: 'contractCode',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  { name: 'Brand', key: 'brandName', type: 'list', category: 'Service/Job', operators: OPS_LIST },
  {
    name: 'Priority',
    key: 'priorityCode',
    type: 'list',
    category: 'Service/Job',
    operators: OPS_LIST,
  },
  {
    name: 'Priority Description',
    key: 'priorityDescription',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Reported Serial',
    key: 'reportedSerial',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Reported Model',
    key: 'reportedModel',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Reported Fault',
    key: 'reportedFault',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Reported Fault Description',
    key: 'reportedFaultDescription',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Creation Notes',
    key: 'creationNotes',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Created At',
    key: 'createdAt',
    type: 'date',
    category: 'Service/Job',
    operators: OPS_DATE_FULL,
  },
  {
    name: 'Created By',
    key: 'createdBy',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Tags',
    key: 'tags',
    type: 'text',
    category: 'Service/Job',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── Status & Allocation ──────────────────────────────────────
  {
    name: 'Status',
    key: 'serviceStatusName',
    type: 'list',
    category: 'Status & Allocation',
    operators: OPS_LIST,
  },
  {
    name: 'Primary Allocation',
    key: 'primaryAllocation',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Primary Allocation Node',
    key: 'primaryAllocationNode',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Secondary Allocation',
    key: 'secondaryAllocation',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Secondary Allocation Node',
    key: 'secondaryAllocationNode',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Scheduled Date',
    key: 'scheduledDate',
    type: 'date',
    category: 'Status & Allocation',
    operators: OPS_DATE_FULL,
  },
  {
    name: 'Follow Up Date',
    key: 'followUpDate',
    type: 'date',
    category: 'Status & Allocation',
    operators: OPS_DATE_FULL,
  },
  {
    name: 'Last Updated At',
    key: 'updatedAt',
    type: 'date',
    category: 'Status & Allocation',
    operators: OPS_DATE_FULL,
  },
  {
    name: 'Last Updated By',
    key: 'updatedBy',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Last Updated Notes',
    key: 'lastUpdatedNote',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Special Scheduled',
    key: 'specialScheduled',
    type: 'boolean',
    category: 'Status & Allocation',
    operators: OPS_BOOLEAN,
  },
  {
    name: 'First Time On Site',
    key: 'firstTimeOnSite',
    type: 'date',
    category: 'Status & Allocation',
    operators: OPS_DATE_FULL,
  },
  {
    name: 'Time Since First On Site',
    key: 'timeSinceFirstOnSite',
    type: 'text',
    category: 'Status & Allocation',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── Structure ────────────────────────────────────────────────
  {
    name: 'Location Name',
    key: 'locationName',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Structure Node Name',
    key: 'structureNodeName',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Structure Node ID',
    key: 'structureNodeId',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_ID,
  },
  {
    name: 'Location Zone',
    key: 'locationZone',
    type: 'list',
    category: 'Structure',
    operators: OPS_LIST,
  },
  {
    name: 'Structure Node Notes',
    key: 'structureNodeNotes',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Sublocation Name',
    key: 'subLocationName',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Sublocation ID',
    key: 'subLocationId',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_ID,
  },
  {
    name: 'Service Address',
    key: 'serviceAddress',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Main Address',
    key: 'mainAddress',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Logistics Address',
    key: 'logisticsAddress',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Region Type',
    key: 'regionType',
    type: 'list',
    category: 'Structure',
    operators: OPS_LIST,
  },
  {
    name: 'Service Contact',
    key: 'serviceContact',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Main Contact',
    key: 'mainContact',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Node Reference #1',
    key: 'nodeReference1',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Node Reference #2',
    key: 'nodeReference2',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Node Reference #3',
    key: 'nodeReference3',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Node Site Schema',
    key: 'nodeSiteSchema',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Trading Hours',
    key: 'tradingHours',
    type: 'text',
    category: 'Structure',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── Reported Part ────────────────────────────────────────────
  {
    name: 'Part Type ID',
    key: 'partTypeId',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_ID,
  },
  {
    name: 'Part Type Name',
    key: 'partTypeName',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Part No.',
    key: 'partNo',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Serial 1',
    key: 'partSerial1',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_BULK,
  },
  {
    name: 'Serial 2',
    key: 'partSerial2',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_BULK,
  },
  {
    name: 'Serial 3',
    key: 'partSerial3',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_BULK,
  },
  {
    name: 'Software Version',
    key: 'softwareVersion',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Firmware Version',
    key: 'firmwareVersion',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Hardware Revision',
    key: 'hardwareRevision',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Fleet Tag',
    key: 'fleetTag',
    type: 'text',
    category: 'Reported Part',
    operators: OPS_TEXT_FULL,
  },

  // ── Linked Records ───────────────────────────────────────────
  {
    name: 'Linked Service #',
    key: 'linkedServices',
    type: 'text',
    category: 'Linked Records',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Linked Shipments',
    key: 'linkedShipments',
    type: 'text',
    category: 'Linked Records',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Linked Shipment Connotes',
    key: 'linkedShipmentConnotes',
    type: 'text',
    category: 'Linked Records',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── Progress ─────────────────────────────────────────────────
  {
    name: 'Days Open',
    key: 'daysOpen',
    type: 'number',
    category: 'Progress',
    operators: OPS_NUM_FULL,
  },
  {
    name: 'Closed At',
    key: 'closedAt',
    type: 'date',
    category: 'Progress',
    operators: OPS_DATE_FULL,
  },
  {
    name: '# of Attendances',
    key: 'attendanceCount',
    type: 'number',
    category: 'Progress',
    operators: OPS_NUM_FULL,
  },
  {
    name: '# of Re-opens',
    key: 'reopensCount',
    type: 'number',
    category: 'Progress',
    operators: OPS_NUM_FULL,
  },
  {
    name: '# of Unique Technicians',
    key: 'uniqueTechniciansCount',
    type: 'number',
    category: 'Progress',
    operators: OPS_NUM_FULL,
  },
  {
    name: 'Observed Fault',
    key: 'observedFault',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Observed Fault Description',
    key: 'observedFaultDescription',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Solution Action',
    key: 'solutionAction',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Solution Action Part Type',
    key: 'solutionActionPartType',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_FULL,
  },
  {
    name: "Serial 1's In",
    key: 'serial1sIn',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Serialised Part Types In',
    key: 'serialisedPartTypesIn',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: "Serial 1's Out",
    key: 'serial1sOut',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Serialised Part Types Out',
    key: 'serialisedPartTypesOut',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Non Serialised Part Types In',
    key: 'nonSerialisedPartTypesIn',
    type: 'text',
    category: 'Progress',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── SLA ──────────────────────────────────────────────────────
  {
    name: 'Client SLA Summary',
    key: 'clientSLASummary',
    type: 'list',
    category: 'SLA',
    operators: OPS_LIST,
  },
  {
    name: 'Ops SLA Summary',
    key: 'opsSLASummary',
    type: 'list',
    category: 'SLA',
    operators: OPS_LIST,
  },

  // ── Interactions ─────────────────────────────────────────────
  {
    name: 'Survey Status',
    key: 'surveyColumns',
    type: 'list',
    category: 'Interactions',
    operators: OPS_LIST,
  },
  {
    // "Parts Pending" isn't a boolean column on the BFF — it's derived from the
    // aggregated `pendedParts` string (pending Parts Request line items). Yes/No
    // therefore maps to `pendedParts isNotEmpty / isEmpty`, not `equals,true`.
    name: 'Parts Pending',
    key: 'pendedParts',
    type: 'boolean',
    presence: true,
    category: 'Interactions',
    operators: OPS_BOOLEAN,
  },

  // ── Billing & Admin ──────────────────────────────────────────
  {
    name: 'Reviewed',
    key: 'reviewed',
    type: 'boolean',
    category: 'Billing & Admin',
    operators: OPS_BOOLEAN,
  },
  {
    name: 'Billable Type',
    key: 'billableType',
    type: 'list',
    category: 'Billing & Admin',
    operators: OPS_LIST,
  },
  {
    name: 'Client Invoiced',
    key: 'clientInvoiced',
    type: 'boolean',
    category: 'Billing & Admin',
    operators: OPS_BOOLEAN,
  },
  {
    name: 'Billing Notes',
    key: 'billingNotes',
    type: 'text',
    category: 'Billing & Admin',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Internal Invoices',
    key: 'internalInvoices',
    type: 'text',
    category: 'Billing & Admin',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'External Invoices',
    key: 'externalInvoices',
    type: 'text',
    category: 'Billing & Admin',
    operators: OPS_TEXT_CONTAINS,
  },

  // ── Misc ─────────────────────────────────────────────────────
  {
    name: 'All Notes',
    key: 'allNotes',
    type: 'text',
    category: 'Misc',
    operators: OPS_TEXT_CONTAINS,
  },
  {
    name: 'Import Session ID',
    key: 'importSessionId',
    type: 'text',
    category: 'Misc',
    operators: OPS_TEXT_FULL,
  },
];

export const FILTER_CATEGORIES = [
  'All',
  'Service/Job',
  'Status & Allocation',
  'Structure',
  'Reported Part',
  'Linked Records',
  'Progress',
  'SLA',
  'Interactions',
  'Billing & Admin',
  'Misc',
];
